import React from 'react';
import { ProvidedModelResponse } from '@scale/shared/types/chat';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { Button, Typography } from '@scale/scaleui';
import { BuilderInputTypes, ObjectSchema } from '@scale/shared/taskTemplate/types';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

const ProvidedModelResponseSchema: ObjectSchema = {
  id: {
    displayName: 'ID',
    required: true,
    inputType: BuilderInputTypes.String,
    hint: 'The ID for the provided model response',
  },
  content: {
    displayName: 'Content',
    required: true,
    inputType: BuilderInputTypes.String,
    hint: 'The content of the provided model response',
  },
  modelEvalEndpointId: {
    displayName: 'Model Eval Endpoint ID',
    required: false,
    inputType: BuilderInputTypes.String,
    hint: 'The eval endpoint id for the provided model response',
  },
  attachments: {
    displayName: 'Attachments',
    required: false,
    inputType: BuilderInputTypes.Attachments,
  },
};

interface ProvidedModelResponsesBuilderProps {
  name: string;
  description?: string;
  existingValue?: Array<ProvidedModelResponse>;
  updateProperty: (arg: Array<ProvidedModelResponse>) => void;
  required?: boolean;
  typeBuilderMap: TypeBuilderMapType;
}

const ProvidedModelResponsesBuilder: React.FC<ProvidedModelResponsesBuilderProps> = ({
  name,
  description,
  existingValue = [],
  updateProperty,
  typeBuilderMap,
}) => {
  const providedModelResponses = existingValue;

  const addProvidedModelResponses = () => {
    const newProvidedModelResponses = [...providedModelResponses];
    const valuePlaceholder = `provided_model_response_${providedModelResponses.length + 1}`;
    newProvidedModelResponses.push({
      id: valuePlaceholder,
      content: valuePlaceholder,
    });
    updateProperty(newProvidedModelResponses);
  };

  return (
    <div className="flex flex-col gap-1">
      {name && <Typography.Subtitle3>{name}</Typography.Subtitle3>}
      {description && <Typography.Subtitle4>{description}</Typography.Subtitle4>}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={providedModelResponses}
        setElements={updateProperty}
        getKey={providedModelResponse => providedModelResponse.id}
        getTitle={providedModelResponse => providedModelResponse.id}
        getSchema={_ => ProvidedModelResponseSchema}
        getWarningOnModify={propertyName =>
          propertyName === 'ID'
            ? "Do not modify ID after it's been saved, this will cause errors in backfills, deliveries, and more."
            : undefined
        }
      />
      <div>
        <Button size="sm" onClick={() => addProvidedModelResponses()} variant="outline">
          Add Provided Response
        </Button>
      </div>
    </div>
  );
};

export default ProvidedModelResponsesBuilder;
